import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faTimesCircle, 
  faSpinner,
  faArrowLeft,
  faGraduationCap,
  faStar,
  faBookOpen,
  faClipboardList
} from '@fortawesome/free-solid-svg-icons';
import Preloader from '../../../components/common/Preloader';
import MetaComponent from '../../../components/common/MetaComponent';
import { getExamPurchaseInfo } from '../../../services/purchase.util';
import { getMaterialPurchaseInfo } from '../../../services/materialsPurchase.util';
import { getMockTestPurchaseInfo } from '../../../services/mockTestsPurchase.util';
import { useAuthContext } from '../../../context/Context';

const PaymentResultPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loggedIn } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [examInfo, setExamInfo] = useState(null);
  const [materialInfo, setMaterialInfo] = useState(null);
  const [mockTestInfo, setMockTestInfo] = useState(null);
  const [countdown, setCountdown] = useState(5);

  const success = searchParams.get('success') === 'true';
  const examId = searchParams.get('examId');
  const materialId = searchParams.get('materialId');
  const mockTestId = searchParams.get('mockTestId');
  const message = searchParams.get('message') || (success ? 'Payment successful!' : 'Payment failed');
  const isMaterialPurchase = Boolean(materialId);
  const isMockTestPurchase = Boolean(mockTestId);

  useEffect(() => {
    const fetchInfo = async () => {
      if (!loggedIn) {
        setLoading(false);
        return;
      }
      try {
        if (examId) {
          const info = await getExamPurchaseInfo(examId);
          setExamInfo(info.data?.exam);
        } else if (materialId) {
          const info = await getMaterialPurchaseInfo(materialId);
          setMaterialInfo(info?.material);
        } else if (mockTestId) {
          const info = await getMockTestPurchaseInfo(mockTestId);
          setMockTestInfo(info?.mockTest);
        }
      } catch (error) {
        console.error('Error fetching purchase info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInfo();
  }, [examId, materialId, mockTestId, loggedIn]);

  // Prevent body scroll when overlay is shown
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!loading) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            if (success) {
              if (examInfo?.slug) navigate(`/exam/${examInfo.slug}`);
              else if (materialInfo?.slug) navigate(`/materials/${materialInfo.slug}`);
              else if (mockTestInfo?.slug) navigate(`/mock-tests/${mockTestInfo.slug}`);
              else navigate(isMockTestPurchase ? '/mock-tests' : isMaterialPurchase ? '/materials' : '/exams');
            } else {
              navigate(isMockTestPurchase ? '/mock-tests' : isMaterialPurchase ? '/materials' : '/exams');
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [loading, success, examInfo, materialInfo, mockTestInfo, isMaterialPurchase, isMockTestPurchase, navigate]);

  const metadata = {
    title: success ? 'Payment Successful || GVK Student Portal' : 'Payment Failed || GVK Student Portal',
    description: success 
      ? 'Your payment was successful. You can now access your purchased exam.'
      : 'Payment could not be processed. Please try again.'
  };

  if (loading) {
    return (
      <div className="main-content">
        <Preloader />
        <div className="text-center py-60">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary mb-20" />
          <h5 className="text-white">Loading payment result...</h5>
        </div>
      </div>
    );
  }

  return (
    <>
      <MetaComponent meta={metadata} />
      <Preloader />
      
      <style>{`
        .payment-result-container {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100vw;
          height: 100vh;
          z-index: 9999;
          background: #000000;
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          overflow: hidden;
          margin: 0;
        }

        .payment-result-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100vw;
          height: 100vh;
          z-index: 9998;
          background: 
            radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
            url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICAgIDxnIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCI+CiAgICAgICAgPGcgZmlsbD0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjAyKSIgZmlsbC1ydWxlPSJub256ZXJvIj4KICAgICAgICAgICAgPGNpcmNsZSBjeD0iNyIgY3k9IjciIHI9IjciLz4KICAgICAgICA8L2c+CiAgICA8L2c+Cjwvc3ZnPg==');
          opacity: 1;
          pointer-events: none;
          animation: backgroundShift 20s ease-in-out infinite;
        }

        .payment-result-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.02) 0%, transparent 50%);
          opacity: 1;
          pointer-events: none;
          animation: backgroundShift 20s ease-in-out infinite;
        }

        @keyframes backgroundShift {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
        }

        .payment-result-wrapper {
          width: 100%;
          max-width: 600px;
          position: relative;
          z-index: 1;
        }

        .payment-result-card {
          background: linear-gradient(135deg, 
            rgba(15, 15, 15, 0.95) 0%, 
            rgba(20, 20, 30, 0.9) 50%, 
            rgba(15, 15, 15, 0.95) 100%
          );
          backdrop-filter: blur(30px) saturate(200%);
          -webkit-backdrop-filter: blur(30px) saturate(200%);
          border-radius: 32px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 
            0 32px 64px rgba(0, 0, 0, 0.5),
            0 0 0 1px rgba(255, 255, 255, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.1),
            inset 0 -1px 0 rgba(0, 0, 0, 0.2);
          position: relative;
          overflow: hidden;
          padding: 60px 50px;
          animation: cardFadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes cardFadeIn {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .payment-result-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(255, 255, 255, 0.3) 20%,
            rgba(255, 255, 255, 0.5) 50%,
            rgba(255, 255, 255, 0.3) 80%,
            transparent 100%
          );
          animation: shimmer 3s ease-in-out infinite;
        }

        @keyframes shimmer {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
        }

        .payment-result-card.success {
          border-color: rgba(255, 255, 255, 0.3);
          box-shadow: 
            0 32px 64px rgba(0, 0, 0, 0.8),
            0 0 0 1px rgba(255, 255, 255, 0.2),
            0 0 80px rgba(255, 255, 255, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.2),
            inset 0 -1px 0 rgba(0, 0, 0, 0.2);
        }

        .payment-result-card.success::after {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 70%);
          animation: successGlow 4s ease-in-out infinite;
          pointer-events: none;
        }

        @keyframes successGlow {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.1);
          }
        }

        .payment-result-card.error {
          border-color: rgba(239, 68, 68, 0.3);
          box-shadow: 
            0 32px 64px rgba(0, 0, 0, 0.5),
            0 0 0 1px rgba(239, 68, 68, 0.2),
            0 0 60px rgba(239, 68, 68, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.1),
            inset 0 -1px 0 rgba(0, 0, 0, 0.2);
        }

        .icon-container {
          position: relative;
          display: inline-block;
          margin-bottom: 40px;
        }

        .icon-wrapper {
          width: 140px;
          height: 140px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          margin: 0 auto;
          animation: iconPulse 2.5s ease-in-out infinite;
        }

        .icon-wrapper.success {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.15) 0%, 
            rgba(255, 255, 255, 0.1) 50%,
            rgba(255, 255, 255, 0.15) 100%
          );
          box-shadow: 
            0 0 0 0 rgba(255, 255, 255, 0.3),
            0 0 0 0 rgba(255, 255, 255, 0.2),
            inset 0 0 0 2px rgba(255, 255, 255, 0.4),
            0 20px 40px rgba(255, 255, 255, 0.15),
            0 0 60px rgba(255, 255, 255, 0.1);
        }

        .icon-wrapper.error {
          background: linear-gradient(135deg, 
            rgba(239, 68, 68, 0.25) 0%, 
            rgba(239, 68, 68, 0.15) 50%,
            rgba(239, 68, 68, 0.25) 100%
          );
          box-shadow: 
            0 0 0 0 rgba(239, 68, 68, 0.4),
            0 0 0 0 rgba(239, 68, 68, 0.3),
            inset 0 0 0 2px rgba(239, 68, 68, 0.4),
            0 20px 40px rgba(239, 68, 68, 0.2);
        }

        @keyframes iconPulse {
          0%, 100% {
            box-shadow: 
              0 0 0 0 rgba(255, 255, 255, 0.3),
              0 0 0 0 rgba(255, 255, 255, 0.2),
              inset 0 0 0 2px rgba(255, 255, 255, 0.4),
              0 20px 40px rgba(255, 255, 255, 0.15),
              0 0 60px rgba(255, 255, 255, 0.1);
            transform: scale(1);
          }
          50% {
            box-shadow: 
              0 0 0 25px rgba(255, 255, 255, 0),
              0 0 0 45px rgba(255, 255, 255, 0),
              inset 0 0 0 2px rgba(255, 255, 255, 0.5),
              0 20px 40px rgba(255, 255, 255, 0.2),
              0 0 80px rgba(255, 255, 255, 0.15);
            transform: scale(1.05);
          }
        }

        .icon-wrapper.error {
          animation: iconPulseError 2.5s ease-in-out infinite;
        }

        @keyframes iconPulseError {
          0%, 100% {
            box-shadow: 
              0 0 0 0 rgba(239, 68, 68, 0.4),
              0 0 0 0 rgba(239, 68, 68, 0.3),
              inset 0 0 0 2px rgba(239, 68, 68, 0.4),
              0 20px 40px rgba(239, 68, 68, 0.2);
            transform: scale(1);
          }
          50% {
            box-shadow: 
              0 0 0 25px rgba(239, 68, 68, 0),
              0 0 0 45px rgba(239, 68, 68, 0),
              inset 0 0 0 2px rgba(239, 68, 68, 0.4),
              0 20px 40px rgba(239, 68, 68, 0.3);
            transform: scale(1.05);
          }
        }

        .icon-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 160px;
          height: 160px;
          border-radius: 50%;
          border: 2px solid;
          opacity: 0.4;
          animation: ringRotate 4s linear infinite;
        }

        .icon-ring.success {
          border-color: rgba(255, 255, 255, 0.4);
          opacity: 0.6;
        }

        .icon-ring.error {
          border-color: rgba(239, 68, 68, 0.6);
        }

        @keyframes ringRotate {
          from {
            transform: translate(-50%, -50%) rotate(0deg);
          }
          to {
            transform: translate(-50%, -50%) rotate(360deg);
          }
        }

        .result-icon {
          font-size: 72px;
          z-index: 2;
          position: relative;
          animation: iconBounce 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.5)) drop-shadow(0 0 20px rgba(255, 255, 255, 0.3));
        }

        @keyframes iconBounce {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.15);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .result-title {
          color: #ffffff !important;
          font-size: 36px !important;
          font-weight: 800 !important;
          margin-bottom: 20px !important;
          letter-spacing: -0.5px !important;
          background: linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.9) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: titleFadeIn 1s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both;
        }

        @keyframes titleFadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .result-message {
          color: rgba(255, 255, 255, 0.75) !important;
          font-size: 18px !important;
          line-height: 1.7 !important;
          margin-bottom: 40px !important;
          font-weight: 400 !important;
          animation: messageFadeIn 1.2s cubic-bezier(0.16, 1, 0.3, 1) 0.4s both;
        }

        @keyframes messageFadeIn {
          from {
            opacity: 0;
            transform: translateY(15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .exam-info-card {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.08) 0%, 
            rgba(255, 255, 255, 0.04) 100%
          );
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 20px;
          padding: 28px 32px;
          margin-bottom: 40px;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          animation: cardSlideIn 1.4s cubic-bezier(0.16, 1, 0.3, 1) 0.6s both;
          position: relative;
          overflow: hidden;
        }

        .exam-info-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(255, 255, 255, 0.1) 50%,
            transparent 100%
          );
          transition: left 0.6s ease;
        }

        .exam-info-card:hover {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.12) 0%, 
            rgba(255, 255, 255, 0.06) 100%
          );
          border-color: rgba(255, 255, 255, 0.2);
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
        }

        .exam-info-card:hover::before {
          left: 100%;
        }

        @keyframes cardSlideIn {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .action-buttons {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 40px;
          animation: buttonsFadeIn 1.6s cubic-bezier(0.16, 1, 0.3, 1) 0.8s both;
        }

        @keyframes buttonsFadeIn {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .result-button {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 12px;
          padding: 20px 40px !important;
          border-radius: 16px !important;
          font-weight: 600 !important;
          font-size: 16px !important;
          letter-spacing: 0.5px !important;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
          text-decoration: none !important;
          position: relative;
          overflow: hidden;
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1.5px solid rgba(255, 255, 255, 0.2) !important;
          box-shadow: 
            0 8px 24px rgba(0, 0, 0, 0.4),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
          will-change: transform, box-shadow;
          cursor: pointer;
        }

        .result-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg, 
            transparent 0%, 
            rgba(255, 255, 255, 0.15) 45%,
            rgba(255, 255, 255, 0.3) 50%, 
            rgba(255, 255, 255, 0.15) 55%,
            transparent 100%
          );
          transition: left 0.7s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .result-button:hover::before {
          left: 100%;
        }

        .result-button.primary {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.1) 0%, 
            rgba(255, 255, 255, 0.05) 50%,
            rgba(255, 255, 255, 0.1) 100%
          ) !important;
          color: #ffffff !important;
          border-color: rgba(255, 255, 255, 0.4) !important;
          text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
        }

        .result-button.primary:hover {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.2) 0%, 
            rgba(255, 255, 255, 0.1) 50%,
            rgba(255, 255, 255, 0.2) 100%
          ) !important;
          transform: translateY(-4px) scale(1.02);
          box-shadow: 
            0 16px 40px rgba(255, 255, 255, 0.2),
            0 0 0 1px rgba(255, 255, 255, 0.5),
            0 0 30px rgba(255, 255, 255, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.3);
          border-color: rgba(255, 255, 255, 0.6) !important;
          color: #ffffff !important;
          text-shadow: 0 0 15px rgba(255, 255, 255, 0.5);
        }

        .result-button.primary:active {
          transform: translateY(-2px) scale(1);
        }

        .result-button.secondary {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.1) 0%, 
            rgba(255, 255, 255, 0.05) 100%
          ) !important;
          color: rgba(255, 255, 255, 0.95) !important;
          border-color: rgba(255, 255, 255, 0.25) !important;
        }

        .result-button.secondary:hover {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.18) 0%, 
            rgba(255, 255, 255, 0.1) 100%
          ) !important;
          transform: translateY(-3px) scale(1.01);
          border-color: rgba(255, 255, 255, 0.4) !important;
          box-shadow: 
            0 12px 32px rgba(0, 0, 0, 0.4),
            inset 0 1px 0 rgba(255, 255, 255, 0.15);
        }

        .result-button.secondary:active {
          transform: translateY(-1px) scale(1);
        }

        .countdown-text {
          color: rgba(255, 255, 255, 0.5) !important;
          font-size: 14px !important;
          margin-top: 32px;
          font-weight: 400 !important;
          animation: countdownFadeIn 1.8s cubic-bezier(0.16, 1, 0.3, 1) 1s both;
        }

        @keyframes countdownFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .countdown-number {
          color: ${success ? '#ffffff' : '#ef4444'} !important;
          font-weight: 700;
          font-size: 18px;
          margin: 0 4px;
          text-shadow: 0 0 10px ${success ? 'rgba(255, 255, 255, 0.5)' : 'rgba(239, 68, 68, 0.6)'};
        }

        .sparkle-icon {
          position: absolute;
          color: rgba(255, 255, 255, 0.6);
          animation: sparkleFloat 4s ease-in-out infinite;
          filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.4));
        }

        .sparkle-icon:nth-child(1) {
          top: 15%;
          left: 8%;
          animation-delay: 0s;
          font-size: 20px;
        }

        .sparkle-icon:nth-child(2) {
          top: 25%;
          right: 12%;
          animation-delay: 1.3s;
          font-size: 16px;
        }

        .sparkle-icon:nth-child(3) {
          bottom: 20%;
          left: 15%;
          animation-delay: 2.6s;
          font-size: 18px;
        }

        .sparkle-icon:nth-child(4) {
          top: 60%;
          right: 8%;
          animation-delay: 0.7s;
          font-size: 14px;
        }

        @keyframes sparkleFloat {
          0%, 100% {
            transform: translateY(0) scale(1) rotate(0deg);
            opacity: 0.4;
          }
          50% {
            transform: translateY(-15px) scale(1.2) rotate(180deg);
            opacity: 0.8;
          }
        }

        @media (max-width: 768px) {
          .payment-result-container {
            padding: 20px 16px;
          }

          .payment-result-card {
            padding: 40px 28px !important;
            border-radius: 24px;
          }

          .icon-wrapper {
            width: 120px;
            height: 120px;
          }

          .icon-ring {
            width: 140px;
            height: 140px;
          }

          .result-icon {
            font-size: 56px;
          }

          .result-title {
            font-size: 28px !important;
          }

          .result-message {
            font-size: 16px !important;
            margin-bottom: 32px !important;
          }

          .exam-info-card {
            padding: 24px 20px;
            margin-bottom: 32px;
          }

          .result-button {
            padding: 18px 32px !important;
            font-size: 15px !important;
          }

          .action-buttons {
            gap: 14px;
            margin-top: 32px;
          }
        }

        @media (max-width: 480px) {
          .payment-result-card {
            padding: 32px 20px !important;
          }

          .icon-wrapper {
            width: 100px;
            height: 100px;
          }

          .result-icon {
            font-size: 48px;
          }

          .result-title {
            font-size: 24px !important;
          }
        }
      `}      </style>
      
      <div className="payment-result-backdrop"></div>
      <div className="payment-result-container">
        <div className="payment-result-wrapper">
          <div className={`payment-result-card ${success ? 'success' : 'error'} text-center`}>
            {/* Decorative stars for success */}
            {success && (
              <>
                <FontAwesomeIcon icon={faStar} className="sparkle-icon" />
                <FontAwesomeIcon icon={faStar} className="sparkle-icon" />
                <FontAwesomeIcon icon={faStar} className="sparkle-icon" />
                <FontAwesomeIcon icon={faStar} className="sparkle-icon" />
              </>
            )}

            {/* Icon with animated background */}
            <div className="icon-container">
              <div className={`icon-wrapper ${success ? 'success' : 'error'}`}>
                <div className={`icon-ring ${success ? 'success' : 'error'}`}></div>
                <FontAwesomeIcon 
                  icon={success ? faCheckCircle : faTimesCircle}
                  className="result-icon"
                  style={{
                    color: success ? '#ffffff' : '#ef4444'
                  }}
                />
              </div>
            </div>

            {/* Title */}
            <h2 className="result-title">
              {success ? 'Payment Successful!' : 'Payment Failed'}
            </h2>

            {/* Message */}
            <p className="result-message">
              {decodeURIComponent(message)}
            </p>

            {/* Exam, Material, or Mock Test Info */}
            {(examInfo || materialInfo || mockTestInfo) && success && (
              <div className="exam-info-card">
                <div className="d-flex align-items-center justify-center gap-15">
                  <FontAwesomeIcon 
                    icon={mockTestInfo ? faClipboardList : materialInfo ? faBookOpen : faGraduationCap} 
                    style={{ 
                      color: '#ffffff',
                      fontSize: '28px',
                      filter: 'drop-shadow(0 2px 8px rgba(255, 255, 255, 0.3))'
                    }} 
                  />
                  <span style={{ 
                    color: '#ffffff', 
                    fontSize: '19px', 
                    fontWeight: '600',
                    letterSpacing: '0.3px'
                  }}>
                    {(mockTestInfo || materialInfo || examInfo).title}
                  </span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="action-buttons">
              {success && (examInfo?.slug || materialInfo?.slug || mockTestInfo?.slug) ? (
                <>
                  <Link
                    to={mockTestInfo?.slug ? `/mock-tests/${mockTestInfo.slug}` : materialInfo?.slug ? `/materials/${materialInfo.slug}` : `/exam/${examInfo.slug}`}
                    className="result-button primary"
                  >
                    <span>{mockTestInfo ? 'Open Mock Tests' : materialInfo ? 'Open Material' : 'Start Exam'}</span>
                    <span style={{ fontSize: '20px' }}>→</span>
                  </Link>
                  <Link
                    to={mockTestInfo ? '/mock-tests' : materialInfo ? '/materials' : '/exams'}
                    className="result-button secondary"
                  >
                    <FontAwesomeIcon icon={mockTestInfo ? faClipboardList : materialInfo ? faBookOpen : faGraduationCap} />
                    <span>Browse More {mockTestInfo ? 'Mock Tests' : materialInfo ? 'Materials' : 'Exams'}</span>
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to={isMockTestPurchase ? '/mock-tests' : isMaterialPurchase ? '/materials' : '/exams'}
                    className="result-button primary"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    <span>Back to {isMockTestPurchase ? 'Mock Tests' : isMaterialPurchase ? 'Materials' : 'Exams'}</span>
                  </Link>
                </>
              )}
            </div>

            {/* Countdown notice */}
            {countdown > 0 && (
              <p className="countdown-text">
                {success && (examInfo?.slug || materialInfo?.slug || mockTestInfo?.slug)
                  ? `Redirecting in `
                  : `Redirecting in `
                }
                <span className="countdown-number">{countdown}</span>
                {countdown === 1 ? ' second' : ' seconds'}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentResultPage;
